import { logger } from "@hopper-b2b/api";

interface TouchDiagnosticEvent {
  eventType: string;
  timestamp: number;
  targetTagName: string;
  targetClassName: string;
  targetId: string;
  touchCount?: number;
  clientX?: number;
  clientY?: number;
  isTrusted: boolean;
  defaultPrevented: boolean;
  bubbles: boolean;
  cancelable: boolean;
  // What element is at the touch point (to detect overlays)
  elementAtPoint?: string;
  elementAtPointClass?: string;
  // Disabled state detection
  isDisabled?: boolean;
  disabledReason?: string;
}

/**
 * Logs detailed touch/click event information for debugging
 * device-specific button click issues.
 */
export function logTouchDiagnostic(
  event: React.TouchEvent | React.MouseEvent | TouchEvent | MouseEvent,
  buttonName: string
) {
  const target = event.target as HTMLElement;

  // Get touch coordinates
  let clientX: number | undefined;
  let clientY: number | undefined;
  let touchCount: number | undefined;

  if ("touches" in event) {
    // Touch event
    touchCount = event.touches.length;
    if (event.touches.length > 0) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else if ("changedTouches" in event && event.changedTouches.length > 0) {
      // For touchend, touches is empty but changedTouches has the info
      clientX = event.changedTouches[0].clientX;
      clientY = event.changedTouches[0].clientY;
    }
  } else if ("clientX" in event) {
    // Mouse event
    clientX = event.clientX;
    clientY = event.clientY;
  }

  // Check what element is at the touch point (to detect overlays)
  let elementAtPoint: string | undefined;
  let elementAtPointClass: string | undefined;
  if (clientX !== undefined && clientY !== undefined) {
    const el = document.elementFromPoint(clientX, clientY);
    if (el) {
      elementAtPoint = el.tagName;
      elementAtPointClass = el.className?.toString?.() || "";
    }
  }

  // Check if the button is disabled
  const { isDisabled, disabledReason } = checkIfDisabled(target);

  const diagnostic: TouchDiagnosticEvent = {
    eventType: event.type,
    timestamp: Date.now(),
    targetTagName: target?.tagName || "unknown",
    targetClassName: target?.className?.toString?.() || "",
    targetId: target?.id || "",
    touchCount,
    clientX,
    clientY,
    isTrusted: event.isTrusted,
    defaultPrevented: event.defaultPrevented,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    isDisabled,
    disabledReason,
    elementAtPoint,
    elementAtPointClass,
  };

  // Log to monitoring system (no console.log in production)
  logger().info(`TouchDiagnostic: ${buttonName}`, {
    event: "touch_diagnostic",
    buttonName,
    ...diagnostic,
  });
}

/**
 * Attaches diagnostic listeners to a button element.
 * Call this in a useEffect with a ref to the button.
 */
export function attachTouchDiagnostics(
  element: HTMLElement | null,
  buttonName: string
): () => void {
  if (!element) return () => {};

  const events = [
    "touchstart",
    "touchend",
    "touchmove",
    "touchcancel",
    "click",
    "mousedown",
    "mouseup",
    "pointerdown",
    "pointerup",
  ];

  const handlers: Array<{ event: string; handler: EventListener }> = [];

  events.forEach((eventName) => {
    const handler = (e: Event) => {
      logTouchDiagnostic(e as TouchEvent | MouseEvent, buttonName);
    };
    element.addEventListener(eventName, handler, { capture: true });
    handlers.push({ event: eventName, handler });
  });

  // Return cleanup function
  return () => {
    handlers.forEach(({ event, handler }) => {
      element.removeEventListener(event, handler, { capture: true });
    });
  };
}

/**
 * Hook to attach touch diagnostics to a button ref
 */
export function useTouchDiagnostics(
  ref: React.RefObject<HTMLElement>,
  buttonName: string,
  enabled = true
) {
  if (typeof window === "undefined") return;

  // Use effect equivalent - attach on mount
  const element = ref.current;
  if (enabled && element) {
    return attachTouchDiagnostics(element, buttonName);
  }
  return () => {};
}

/**
 * Check what elements are stacked on top of a given element.
 * Useful for detecting invisible overlays blocking clicks.
 */
export function logElementStack(
  element: HTMLElement,
  buttonName: string
): void {
  const rect = element.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  // Get all elements at the center point
  const elementsAtPoint = document.elementsFromPoint(centerX, centerY);

  const stack = elementsAtPoint.map((el, index) => ({
    index,
    tagName: el.tagName,
    className: el.className?.toString?.() || "",
    id: el.id || "",
    zIndex: window.getComputedStyle(el).zIndex,
    position: window.getComputedStyle(el).position,
    pointerEvents: window.getComputedStyle(el).pointerEvents,
  }));

  // Find where our button is in the stack
  const buttonIndex = elementsAtPoint.indexOf(element);

  const bodyPointerEvents = document.body.style.pointerEvents;
  const bodyComputedPointerEvents = window.getComputedStyle(
    document.body
  ).pointerEvents;
  const hasBlockInteractivityClass = Array.from(document.body.classList).some(
    (cls) => cls.startsWith("block-interactivity-")
  );

  const diagnostic = {
    buttonName,
    buttonIndex,
    totalElements: stack.length,
    elementsAboveButton: buttonIndex > 0 ? stack.slice(0, buttonIndex) : [],
    buttonRect: {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    },
    bodyPointerEvents: bodyPointerEvents || "unset",
    bodyComputedPointerEvents,
    hasBlockInteractivityClass,
  };

  logger()?.info(`ElementStack: ${buttonName}`, {
    event: "element_stack",
    ...diagnostic,
    fullStack: stack,
  });
}

/**
 * Checks if an element or its parent button is disabled.
 * Checks multiple ways a button can be disabled.
 */
function checkIfDisabled(element: HTMLElement | null | undefined): {
  isDisabled: boolean;
  disabledReason: string;
} {
  // Handle null/undefined element (e.g., from mock events)
  if (!element) {
    return {
      isDisabled: false,
      disabledReason: "unknown (no element)",
    };
  }

  const reasons: string[] = [];

  // Find the button element (could be the target or a parent)
  let buttonElement: HTMLElement | null = element;
  while (buttonElement && buttonElement.tagName !== "BUTTON") {
    buttonElement = buttonElement.parentElement;
  }

  const elementsToCheck: HTMLElement[] = [element];
  if (buttonElement && buttonElement !== element) {
    elementsToCheck.push(buttonElement);
  }

  for (const el of elementsToCheck) {
    const tagName = el.tagName;

    // Check disabled attribute
    if (el.hasAttribute("disabled")) {
      reasons.push(`${tagName} has disabled attribute`);
    }

    // Check aria-disabled
    if (el.getAttribute("aria-disabled") === "true") {
      reasons.push(`${tagName} has aria-disabled=true`);
    }

    // Check for disabled class names
    const className = el.className?.toString?.() || "";
    if (
      className.includes("disabled") ||
      className.includes("Mui-disabled") ||
      className.includes("Mui-focusVisible")
    ) {
      reasons.push(`${tagName} has disabled-related class: ${className}`);
    }

    // Check CSS pointer-events
    const computedStyle = window.getComputedStyle(el);
    if (computedStyle.pointerEvents === "none") {
      reasons.push(`${tagName} has pointer-events: none`);
    }

    // Check CSS visibility/display
    if (computedStyle.visibility === "hidden") {
      reasons.push(`${tagName} has visibility: hidden`);
    }
    if (computedStyle.display === "none") {
      reasons.push(`${tagName} has display: none`);
    }

    // Check opacity (might indicate disabled state)
    if (parseFloat(computedStyle.opacity) < 0.5) {
      reasons.push(`${tagName} has low opacity: ${computedStyle.opacity}`);
    }
  }

  return {
    isDisabled: reasons.length > 0,
    disabledReason: reasons.join("; ") || "none",
  };
}
